import React, { useState } from 'react';
import axios from 'axios';
import { API_URL, FILE_URL, VIDEO_THUMBNAIL } from '../constants';
import { Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

const allowedMediaTypes = ['mp4', 'jpg', 'jpeg', 'png', 'webp', 'gif', 'pdf'];

const FileUploadLibrary = ({ select = 'single', onClick, authToken }) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState({});
    const [errorFile, setErrorFile] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const MAX_FILE_SIZE_MB = 5;

    const handleFileChange = async (e) => {
        const selected = Array.from(e.target.files);
        setErrorFile(null);

        for (let file of selected) {
            const ext = file.name.split('.').pop().toLowerCase();
            if (!allowedMediaTypes.includes(ext)) {
                setErrorFile(`Unsupported file type: ${ext}`);
                continue;
            }

            if (file.size > 5 * 1024 * 1024) {
                toast.error(`${file.name} exceeds the 5MB size limit.`);
                continue;
            }

            const tempId = Date.now() + Math.random();
            const tempPreview = {
                id: tempId,
                blobUrl: URL.createObjectURL(file),
                uploading: true,
                ext,
            };

            setFiles((prev) => [...prev, tempPreview]);

            const formData = new FormData();
            formData.append('file', file);

            try {
                const res = await axios.post(`${API_URL}/media/upload`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${authToken}`,
                    },
                    onUploadProgress: (e) => {
                        const percent = Math.round((e.loaded * 100) / e.total);
                        setUploading((prev) => ({
                            ...prev,
                            [tempId]: { status: 'uploading', progress: percent }
                        }));
                    },
                });

                const { data } = res;

                if (data.status === 'failed') {
                    throw new Error(data.error || 'Upload failed');
                }

                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === tempId
                            ? {
                                ...f,
                                id: data.id,
                                mediaFilename: data.mediaFilename,
                                mediaExtension: data.ext,
                                mediaLocation: data.mediaLocation,
                                blobUrl: null,
                                uploading: false,
                            }
                            : f
                    )
                );

                setUploading((prev) => ({
                    ...prev,
                    [tempId]: { status: 'success', progress: 100 },
                }));
                
                toast.success(`${file.name} uploaded successfully!`);
            } catch (err) {
                const errorMsg = err.response?.data?.error || err.message || 'Failed to upload';
                setUploading((prev) => ({
                    ...prev,
                    [tempId]: {
                        status: 'failed',
                        progress: 0,
                        error: errorMsg,
                    },
                }));
                toast.error(`${file.name}: ${errorMsg}`);
            }
        }
    };

    const handleSelect = (file) => {
        const alreadySelected = selectedFile?.id === file.id;
        const selection = alreadySelected ? null : file;
        setSelectedFile(selection);

        if (onClick && !alreadySelected) onClick(file);
    };

    const getThumb = (file) => {
        if (file.blobUrl) return file.blobUrl;
        if (file.mediaExtension === 'mp4') return VIDEO_THUMBNAIL;
        return `${FILE_URL}/uploads/${file.mediaLocation}/${file.mediaFilename}-thumbnail.${file.mediaExtension}`;
    };

    return (
        <div className="h-100">
            {errorFile && <div className="alert alert-danger">{errorFile}</div>}

            <fieldset className="upload_dropZone text-center mb-3 p-4">
                <legend className="visually-hidden">File uploader</legend>
                <p className="small my-2">
                    Drag &amp; Drop media files (Images, Video, PDF) or click below to select<br />
                    <strong>Max file size: 5MB each</strong>
                </p>
                <input
                    id="file-upload"
                    className="custom-file form-control position-absolute invisible"
                    type="file"
                    multiple
                    accept={allowedMediaTypes.map(type => '.' + type).join(',')}
                    onChange={handleFileChange}
                />
                <label className="btn btn-upload mb-3" htmlFor="file-upload">
                    Choose file(s)
                </label>
            </fieldset>

            <div className="row row-cols-3 row-cols-lg-6 row-cols-md-5 g-2 media_loader_gallery sticky-toprow row-cols-2 row-cols-md-4 g-3">
                {files.map((file) => (
                    <div key={file.id} className="col position-relative">
                        <div className="file-preview-wrapper">
                            <img
                                src={getThumb(file)}
                                alt="Preview"
                                className="img-thumbnail"
                                width={100}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleSelect(file)}
                            />
                            {selectedFile?.id === file.id && (
                                <Form.Check
                                    type="checkbox"
                                    className="position-absolute top-0 end-0 p-2"
                                    checked
                                    readOnly
                                />
                            )}

                            {file.uploading && (
                                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75">
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            )}
                        </div>

                        {uploading[file.id]?.status === 'failed' && (
                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-danger bg-opacity-90 rounded" style={{fontSize: '11px'}}>
                                <span className="text-white text-center fw-bold p-2">Upload Failed</span>
                                <small className="text-white text-center p-2" style={{fontSize: '9px'}}>{uploading[file.id].error}</small>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FileUploadLibrary;
